/**
 * Voice/video CALL overlay (WebRTC): calling / incoming (accept-reject)
 * / in progress (mute · camera · hang up) / video with local camera in PiP.
 *
 * The media is encrypted by DTLS-SRTP and the signaling by the E2E ratchet (blind
 * relay). UI in Susurro's language (glass plum, periwinkle accent, encryption
 * teal, destructive for hang up) with a reduced-motion guard.
 */

import { createEffect, Match, Show, Switch } from "solid-js";
import { Lock, Mic, MicOff, Phone, PhoneOff, Video, VideoOff } from "@fleet/icons";
import { msg } from "@lingui/core/macro";

import { useChat } from "@/providers/chat-provider";
import { useI18n } from "@/providers/i18n-provider";

function StreamVideo(props: {
  stream?: MediaStream;
  muted?: boolean;
  testid: string;
  class?: string;
}) {
  return (
    <video
      data-testid={props.testid}
      autoplay
      playsinline
      muted={props.muted}
      class={props.class}
      ref={(el) => {
        createEffect(() => {
          el.srcObject = props.stream ?? null;
        });
      }}
    />
  );
}

export function CallOverlay() {
  const chat = useChat();
  const { t } = useI18n();
  const state = () => chat.callState();
  const info = () => chat.callInfo();
  const isVideo = () => info()?.media === "video";
  const peer = () => info()?.peerDeviceId ?? "";

  return (
    <Show when={state() !== "idle"}>
      <div
        data-testid="call-overlay"
        data-state={state()}
        data-media={info()?.media}
        class="fixed inset-0 z-50 flex flex-col items-center justify-between bg-[var(--background)]/95 p-6 backdrop-blur-xl"
        role="dialog"
        aria-label={t(msg`Call`)}
      >
        {/* Vídeo remoto a pantalla (o avatar para audio) */}
        <div class="relative flex flex-1 items-center justify-center overflow-hidden">
          <Show
            when={isVideo() && chat.remoteStream()}
            fallback={
              <div class="flex flex-col items-center gap-4">
                <div
                  class="flex h-28 w-28 items-center justify-center rounded-full text-[var(--accent-foreground)] motion-safe:animate-pulse"
                  style={{
                    background:
                      "linear-gradient(135deg, oklch(0.72 0.13 278), oklch(0.66 0.12 210))",
                  }}
                >
                  <Phone class="h-12 w-12" />
                </div>
              </div>
            }
          >
            <StreamVideo
              testid="remote-video"
              stream={chat.remoteStream()}
              class="max-h-full max-w-full rounded-2xl object-contain"
            />
          </Show>
          {/* Audio remoto siempre (aunque sea videollamada, el <video> ya lo lleva;
              para llamada de voz montamos un <audio> para oír al peer). */}
          <Show when={!isVideo() && chat.remoteStream()}>
            <StreamVideo
              testid="remote-audio-carrier"
              stream={chat.remoteStream()}
              class="hidden"
            />
          </Show>
          {/* Cámara local en PiP */}
          <Show when={isVideo() && chat.localStream()}>
            <div class="absolute right-3 bottom-3 h-32 w-24 overflow-hidden rounded-xl border border-[var(--glass-border)] shadow-lg">
              <StreamVideo
                testid="local-video"
                stream={chat.localStream()}
                muted
                class="h-full w-full object-cover"
              />
            </div>
          </Show>
        </div>

        {/* Estado + identidad */}
        <div class="flex flex-col items-center gap-1.5 text-center">
          <div class="max-w-xs truncate font-mono text-sm text-foreground">{peer()}</div>
          <div
            data-testid="call-status"
            class="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
          >
            <Lock class="h-3 w-3 text-success" />
            <Switch>
              <Match when={state() === "calling"}>{t(msg`Calling…`)}</Match>
              <Match when={state() === "incoming"}>
                {isVideo() ? t(msg`Incoming video call`) : t(msg`Incoming voice call`)}
              </Match>
              <Match when={state() === "connecting"}>{t(msg`Connecting…`)}</Match>
              <Match when={state() === "connected"}>{t(msg`Encrypted call`)}</Match>
              <Match when={state() === "ended"}>{t(msg`Call ended`)}</Match>
            </Switch>
          </div>
        </div>

        {/* Controles */}
        <div class="flex items-center justify-center gap-5 pb-2">
          <Switch>
            <Match when={state() === "incoming"}>
              <button
                type="button"
                data-testid="call-reject"
                aria-label={t(msg`Reject`)}
                class="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--destructive)] text-[var(--destructive-foreground)] transition-transform hover:scale-105 focus:ring-2 focus:ring-ring/60 focus:outline-none"
                onClick={() => void chat.rejectCall()}
              >
                <PhoneOff class="h-6 w-6" />
              </button>
              <button
                type="button"
                data-testid="call-accept"
                aria-label={t(msg`Accept`)}
                class="flex h-16 w-16 items-center justify-center rounded-full bg-success text-[var(--success-foreground)] transition-transform hover:scale-105 focus:ring-2 focus:ring-ring/60 focus:outline-none"
                onClick={() => void chat.acceptCall()}
              >
                <Phone class="h-6 w-6" />
              </button>
            </Match>
            <Match when={state() === "connected" || state() === "connecting"}>
              <button
                type="button"
                data-testid="call-mute"
                aria-pressed={info()?.muted}
                aria-label={info()?.muted ? t(msg`Unmute`) : t(msg`Mute`)}
                class="flex h-14 w-14 items-center justify-center rounded-full border border-[var(--glass-border)] bg-[var(--surface-secondary)] text-foreground transition-transform hover:scale-105 focus:ring-2 focus:ring-ring/60 focus:outline-none"
                onClick={() => chat.toggleMute()}
              >
                <Show when={info()?.muted} fallback={<Mic class="h-5 w-5" />}>
                  <MicOff class="h-5 w-5" />
                </Show>
              </button>
              <Show when={isVideo()}>
                <button
                  type="button"
                  data-testid="call-camera"
                  aria-pressed={info()?.cameraOff}
                  aria-label={info()?.cameraOff ? t(msg`Turn camera on`) : t(msg`Turn camera off`)}
                  class="flex h-14 w-14 items-center justify-center rounded-full border border-[var(--glass-border)] bg-[var(--surface-secondary)] text-foreground transition-transform hover:scale-105 focus:ring-2 focus:ring-ring/60 focus:outline-none"
                  onClick={() => chat.toggleCamera()}
                >
                  <Show when={info()?.cameraOff} fallback={<Video class="h-5 w-5" />}>
                    <VideoOff class="h-5 w-5" />
                  </Show>
                </button>
              </Show>
              <button
                type="button"
                data-testid="call-hangup"
                aria-label={t(msg`Hang up`)}
                class="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--destructive)] text-[var(--destructive-foreground)] transition-transform hover:scale-105 focus:ring-2 focus:ring-ring/60 focus:outline-none"
                onClick={() => void chat.hangupCall()}
              >
                <PhoneOff class="h-6 w-6" />
              </button>
            </Match>
            <Match when={state() === "calling"}>
              <button
                type="button"
                data-testid="call-hangup"
                aria-label={t(msg`Cancel`)}
                class="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--destructive)] text-[var(--destructive-foreground)] transition-transform hover:scale-105 focus:ring-2 focus:ring-ring/60 focus:outline-none"
                onClick={() => void chat.hangupCall()}
              >
                <PhoneOff class="h-6 w-6" />
              </button>
            </Match>
          </Switch>
        </div>
      </div>
    </Show>
  );
}
